/* eslint-disable no-undef */
import Command from '@ckeditor/ckeditor5-core/src/command';
import { CkeditorConstants, getLinkTextNodeInSelection, isLinkElement } from '../../src/utils';

const {
  htmlElement: { LINK_SPAN }
} = CkeditorConstants;

export default class NumberedCitationCommand extends Command {
  execute({ value }) {
    const editor = this.editor;
    let subsequentCitedAttributes = {};
    let parentCitationCount = null;
    if (value.count) {
      parentCitationCount = value.count;
      delete value.count;
    }
    const selection = editor.model.document.selection;
    const citationCount = document.querySelectorAll('.numbered-citation').length;
    if (value['data-parentcitation']) {
      const editingRootDomElement = editor.editing.view.domRoots.get('main').querySelector(`[id='${value['data-parentcitation']}']`);
      const baseHrefId = this.editor?.baseHrefId?.concat('#') || '';
      const baseHrefPathUrl = this.editor?.baseHrefPathUrl?.concat('/') || '';
      const aboutAttribute = `${baseHrefPathUrl}${baseHrefId}${value.id}`;
      subsequentCitedAttributes = {
        'resource': editingRootDomElement.getAttribute('resource'),
        'about': aboutAttribute,
        'typeof': editingRootDomElement.getAttribute('typeof'),
        'rel': editingRootDomElement.getAttribute('rel')
      };
    }
    editor.model.change((writer) => {
      const placeholder = writer.createElement('numberedcitation', {
        'data-citationcount': parentCitationCount ? parentCitationCount : (citationCount + 1),
        id: value.id,
        ...subsequentCitedAttributes,
        ...value
      });

      const firstPosition = selection.getFirstPosition();
      /**
       * Find the nearest selected/ancestor link container where citation nesting must be avoided.
       * We consider both model linkspan and rel="pcicore:linksTo" elements.
       */
      const selectedElement = selection.getSelectedElement?.();
      const ancestorElements = firstPosition?.parent?.getAncestors?.() || [];
      const linkContainer = [selectedElement, firstPosition?.parent, ...ancestorElements].find(
        (element) => element && (element.name === LINK_SPAN || isLinkElement(element))
      );

      /**
        * Resolve a link-related text node from the current selection.
        * - If present, the command treats selection as fresh-link/text-link context.
        * - If absent, selection is handled as container-only link context.
        * This flag controls whether insertion uses text-node boundaries or link-container boundaries.
       */
      const linkTextNodeInSelection = getLinkTextNodeInSelection(this.editor);
      const isTextWithLinkHrefClicked = !linkTextNodeInSelection;

      let linkHrefRange = null, linkHrefStart = null, linkHrefEnd = null;
      if (linkTextNodeInSelection) {
        const linkRangeTarget = linkTextNodeInSelection.textNode || linkTextNodeInSelection;
        linkHrefRange = writer.createRangeOn(linkRangeTarget);
        linkHrefStart = linkHrefRange.start;
        linkHrefEnd = linkHrefRange.end;
      } 
      /**
       * Insert citations as siblings (never inside link containers):
       * - at link start boundary  -> insert before link
       * - at link end boundary    -> insert after link
       */
      let insertAt = selection;
      if (linkContainer) {
        if (isTextWithLinkHrefClicked) {
          insertAt = writer.createPositionAfter(linkContainer);
        } else {
          const linkStart = writer.createPositionAt(linkContainer, 0);
          const isAtLinkStart = firstPosition?.isEqual(linkStart);

          insertAt = isAtLinkStart
            ? writer.createPositionBefore(linkContainer)
            : writer.createPositionAfter(linkContainer);
        }
      } else if (linkHrefRange && linkHrefStart && linkHrefEnd) {
        const isAtLinkStart = firstPosition?.isEqual(linkHrefStart);
        insertAt = isAtLinkStart
          ? linkHrefStart
          : linkHrefEnd;
      }

      this.editor.model.insertContent(
        placeholder,
        insertAt
      );
      writer.setSelection(placeholder, 'on');
    });

    editor.execute('renumbercitations');
    editor.execute('numberedcitationconverter');

    editor.commands.get('save').isEnabled = true;
    editor.commands.get('save').hasUnsavedChanges =
      true;
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;
    const isAllowed = model.schema.checkChild(selection.focus.parent, 'numberedcitation');
    this.isEnabled = isAllowed;
  }
}
